/**
 * 관 정보 상세 페이지
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Spinner, Tabs, Tab, Table, Form, Alert } from 'react-bootstrap';
import AdminLayout from '../../../components/layout/AdminLayout';
import { productService } from '../../../services/productService';
import { hallMediaService } from '../../../services/hallMediaService';
import { Hall, HallMedia, ProductPrice } from '../../../types/product';

const HallDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hallId = id ? parseInt(id, 10) : 0;
  const [activeTab, setActiveTab] = useState<string>('info');

  const { data: hall, isLoading } = useQuery({
    queryKey: ['hall', hallId],
    queryFn: () => productService.getHall(hallId),
    enabled: !!hallId && hallId > 0,
  });

  const { data: mediaList } = useQuery({
    queryKey: ['hallMedia', hallId],
    queryFn: () => hallMediaService.getHallMedia(hallId),
    enabled: !!hallId && hallId > 0,
  });

  const { data: pricesData } = useQuery({
    queryKey: ['hallPrices', hallId],
    queryFn: () => productService.getPrices({ owner_type: 'hall', owner_id: hallId }),
    enabled: !!hallId && hallId > 0,
  });

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!hall) {
    return (
      <AdminLayout>
        <Card>
          <Card.Body>
            <p>관 정보를 찾을 수 없습니다.</p>
            <Button variant="secondary" onClick={() => navigate('/admin/products/halls')}>
              목록으로
            </Button>
          </Card.Body>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>관 정보 상세</h1>
          <div>
            <Button variant="secondary" onClick={() => navigate('/admin/products/halls')} className="me-2">
              목록으로
            </Button>
            <Button variant="primary" onClick={() => navigate(`/admin/products/halls/${hallId}/edit`)}>
              수정
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <div className="mb-3">
              <h3>{hall.name}</h3>
              <div className="mt-2">
                <Badge bg={hall.status === 'active' ? 'success' : 'secondary'}>
                  {hall.status === 'active' ? '활성' : '비활성'}
                </Badge>
              </div>
            </div>

            {hall.description && (
              <div className="mb-3">
                <strong>설명:</strong>
                <div style={{ 
                  padding: '15px', 
                  backgroundColor: '#2E2E3A', 
                  borderRadius: '5px',
                  color: '#ccc',
                  marginTop: '10px',
                  whiteSpace: 'pre-wrap'
                }}>
                  {hall.description}
                </div>
              </div>
            )}

            {hall.capacity && (
              <div className="mb-3">
                <strong>수용인원:</strong> {hall.capacity}명
              </div>
            )}

            <div className="mb-3">
              <strong>등록일:</strong> {new Date(hall.created_at).toLocaleString('ko-KR')}
            </div>

            {hall.updated_at && (
              <div className="mb-3">
                <strong>수정일:</strong> {new Date(hall.updated_at).toLocaleString('ko-KR')}
              </div>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'info')} className="mb-3">
              <Tab eventKey="info" title="기본 정보">
                <div className="mt-3">
                  <p>기본 정보는 위의 카드에 표시됩니다.</p>
                </div>
              </Tab>

              <Tab eventKey="media" title="미디어 콘텐츠">
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>관별 미디어 콘텐츠</h5>
                    <Button variant="primary" size="sm">
                      미디어 추가
                    </Button>
                  </div>

                  {mediaList && mediaList.length > 0 ? (
                    <Table striped bordered hover variant="dark">
                      <thead>
                        <tr>
                          <th>순서</th>
                          <th>유형</th>
                          <th>제목</th>
                          <th>URL</th>
                          <th>대표</th>
                          <th>상태</th>
                          <th>작업</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mediaList.map((media) => (
                          <tr key={media.id}>
                            <td>{media.display_order}</td>
                            <td>
                              {media.media_type === 'image' && <Badge bg="info">이미지</Badge>}
                              {media.media_type === 'video' && <Badge bg="warning">동영상</Badge>}
                              {media.media_type === 'youtube' && <Badge bg="danger">YouTube</Badge>}
                            </td>
                            <td>{media.title || '-'}</td>
                            <td>
                              <a href={media.media_url} target="_blank" rel="noopener noreferrer" style={{ color: '#00BCD4' }}>
                                {media.media_url.substring(0, 50)}...
                              </a>
                            </td>
                            <td>{media.is_main ? <Badge bg="primary">대표</Badge> : '-'}</td>
                            <td>
                              <Badge bg={media.status === 'active' ? 'success' : 'secondary'}>
                                {media.status === 'active' ? '활성' : '비활성'}
                              </Badge>
                            </td>
                            <td>
                              <Button variant="sm" size="sm" className="me-1">수정</Button>
                              <Button variant="danger" size="sm">삭제</Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <Alert variant="info">등록된 미디어 콘텐츠가 없습니다.</Alert>
                  )}
                </div>
              </Tab>

              <Tab eventKey="prices" title="가격 정보">
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>관별 가격 정보</h5>
                    <Button variant="primary" size="sm">
                      가격 추가
                    </Button>
                  </div>

                  {pricesData && pricesData.prices.length > 0 ? (
                    <Table striped bordered hover variant="dark">
                      <thead>
                        <tr>
                          <th>카테고리</th>
                          <th>가격 유형</th>
                          <th>가격</th>
                          <th>최소 수량</th>
                          <th>유효 기간</th>
                          <th>상태</th>
                          <th>작업</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pricesData.prices.map((price) => (
                          <tr key={price.id}>
                            <td>
                              {price.price_category === 'base' && <Badge bg="primary">기본</Badge>}
                              {price.price_category === 'group' && <Badge bg="info">단체</Badge>}
                              {price.price_category === 'promotion' && <Badge bg="warning">프로모션</Badge>}
                            </td>
                            <td>
                              {price.price_type === 'adult' && '성인'}
                              {price.price_type === 'youth' && '청소년'}
                              {price.price_type === 'disabled' && '장애인'}
                              {price.price_type === 'infant' && '유아'}
                            </td>
                            <td>{price.price_amount.toLocaleString()}원</td>
                            <td>{price.min_quantity || '-'}</td>
                            <td>
                              {price.valid_from && price.valid_to ? (
                                <div>
                                  {new Date(price.valid_from).toLocaleDateString('ko-KR')} ~ {new Date(price.valid_to).toLocaleDateString('ko-KR')}
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td>
                              <Badge bg={price.status === 'active' ? 'success' : 'secondary'}>
                                {price.status === 'active' ? '활성' : '비활성'}
                              </Badge>
                            </td>
                            <td>
                              <Button variant="sm" size="sm" className="me-1">수정</Button>
                              <Button variant="danger" size="sm">삭제</Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <Alert variant="info">등록된 가격 정보가 없습니다.</Alert>
                  )}
                </div>
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default HallDetail;

